import { useAnalytics } from "../hooks/useAnalytics";
import { usePagination } from "../hooks/usePagination";
import { usePresetStats } from "../hooks/usePresetStats";
import { AnalyticsChart } from "./AnalyticsChart";
import { AnalyticsPresetTable } from "./AnalyticsPresetTable";
import { AnalyticsSummary } from "./AnalyticsSummary";
import { useState, useMemo, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { DateRangePicker } from "@/shared/widgets/DateRangePicker";
import { useDefaultDatePresets } from "@/shared/widgets/DateRangePicker/presets";
import {
  CreatorPresetStats,
  PackPresetStats,
} from "../model/presetStats.types";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { SortOption } from "../model/analytics.types";
import { useTranslation } from "@/shared/i18n";

export default function AnalyticsPage() {
  const { t } = useTranslation('analytics');
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      from: firstDayOfMonth,
      to: today,
    };
  });
  const datePresets = useDefaultDatePresets();
  const [activeTab, setActiveTab] = useState<'pack' | 'creator'>('creator');
  const [sortBy, setSortBy] = useState<SortOption>('paidUses');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { currentPage, setCurrentPage, limit, setLimit, totalPages, setTotalPages } =
    usePagination({ initialPage: 1, initialLimit: 20 });

  const { data, isLoading, error, refetch } = usePresetStats({
    type: activeTab, // "pack" ou "creator"
    dateRange,
    page: currentPage,
    limit,
    sortBy,
    sortOrder,
  });


  const handlePageSizeChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1);
  }, [setLimit, setCurrentPage]);

  useEffect(() => {
    if (data?.meta?.totalPages) {
      setTotalPages(data.meta.totalPages);
    }
  }, [data?.meta?.totalPages, setTotalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, setCurrentPage]);

  const { packData, creatorData, packMeta, creatorMeta } = useMemo(() => ({
    packData: data?.data as PackPresetStats[],
    creatorData: data?.data as CreatorPresetStats[],
    packMeta: data?.meta,
    creatorMeta: data?.meta,
  }), [data?.data]);

  const handleRefetch = useCallback(async () => {
    await refetch();
  }, [sortBy, sortOrder, refetch]);

  const presetError = error;

  const {
    summary,
    usage,
    loading,
    errors,
  } = useAnalytics({
    page: currentPage,
    limit,
    ...(dateRange?.from && { start: format(dateRange.from, "yyyy-MM-dd") }),
    ...(dateRange?.to && { end: format(dateRange.to, "yyyy-MM-dd") }),
  });


  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    if (range?.from) {
      setDateRange({
        from: range.from,
        to: range.to
      });
    } else {
      // Se não tem from, reseta para o padrão (primeiro dia do mês até hoje)
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateRange({
        from: firstDayOfMonth,
        to: today,
      });
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, setCurrentPage]);
  useEffect(() => {
    if (presetError) {
      console.error('Error fetching preset stats:', presetError);
    }
  }, [presetError]);
  return (
    <PageContent title={t('dashboardStats')} items={[
      {
        label: t('dashboard'),
        href: "/",
      },

    ]}>
      <div className="min-h-screen bg-[#1A1A1A] text-white relative">
        <div className="sticky top-16 z-20 bg-[#1A1A1A] py-4">
          <div className="flex justify-end">
            <DateRangePicker className="w-full md:w-[280px]" dateRange={dateRange} setDateRange={handleDateRangeChange} presets={datePresets} />
          </div>
        </div>

        <AnalyticsSummary
          summary={summary}
          loading={loading.summary}
          error={errors.summary}
        />

        <AnalyticsChart
          usage={usage}
          loading={loading.usage}
          error={errors.usage}
        />

        <div className="mt-8 mb-8">
          <h3 className="text-xl font-semibold mb-4">{t('presetStats')}</h3>
          <AnalyticsPresetTable
            packData={packData}
            packMeta={packMeta}
            creatorData={creatorData}
            creatorMeta={creatorMeta}
            loadingPack={isLoading}
            loadingCreator={isLoading}
            error={presetError}
            onRefresh={handleRefetch}
            currentPage={currentPage}
            limit={limit}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
        </div>

      </div>
    </PageContent>
  );
}
